"use client";
import React, { useEffect, useState, useCallback, useRef } from 'react';
import Image from "next/image";
import { getArticleListByCategory } from '@/services/publicService';
import { convertIntoSubString } from '@/services/constantService';
import Link from 'next/link';

const LearningCenterClient = ({ initialArticles, initialTab = "", categories = [] }) => {
    // Ensure props are always the expected shape to prevent hydration mismatches
    const safeCategories = Array.isArray(categories) ? categories.filter((category) => category?.slug) : [];
    const safeInitialArticles = Array.isArray(initialArticles) ? initialArticles : [];

    const [activeTab, setActiveTab] = useState(initialTab);
    const [articleList, setArticleList] = useState(safeInitialArticles);
    const isMounted = useRef(false);

    // Index of the currently active category (defaults to the first tab)
    const activeIndex = Math.max(
        0,
        safeCategories.findIndex((category) => category.slug === activeTab)
    );

    // Get Article List for the active category
    const getArticleList = useCallback(async () => {
        if (!activeTab) return;
        const response = await getArticleListByCategory(activeTab);
        // Handle different response structures: could be array directly, or { data: [...] }
        const articles = Array.isArray(response) ? response : (response?.data || []);
        setArticleList(articles);
    }, [activeTab]);

    const handleTabClick = (index) => {
        const category = safeCategories[index];
        if (!category || category.slug === activeTab) {
            return;
        }
        setActiveTab(category.slug);
    };

    // Mobile carousel arrow handlers — wrap around the available tabs
    const goPrevTab = () => {
        if (safeCategories.length === 0) return;
        handleTabClick((activeIndex - 1 + safeCategories.length) % safeCategories.length);
    };
    const goNextTab = () => {
        if (safeCategories.length === 0) return;
        handleTabClick((activeIndex + 1) % safeCategories.length);
    };

    useEffect(() => {
        // Mark as mounted after first render
        isMounted.current = true;
    }, []);

    useEffect(() => {
        // Only fetch if tab changed after mount (not during initial hydration)
        if (isMounted.current) {
            getArticleList();
        }
    }, [activeTab, getArticleList]);

    return (
        <>
            <div className="tabs-container d-flex justify-content-around align-items-center">
                <button
                    type="button"
                    className="tabs-arrow tabs-arrow-prev"
                    aria-label="Previous category"
                    onClick={goPrevTab}
                >
                    <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 1L1 8L8 15" stroke="#0D2469" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>

                {safeCategories.map((category, index) => (
                    <button
                        key={category.id ?? category.slug}
                        className={`tab-button ${activeTab === category.slug ? "active" : "inactive"}`}
                        onClick={() => handleTabClick(index)}
                    >
                        {category.name}
                    </button>
                ))}

                <button
                    type="button"
                    className="tabs-arrow tabs-arrow-next"
                    aria-label="Next category"
                    onClick={goNextTab}
                >
                    <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 1L9 8L2 15" stroke="#0D2469" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
            </div>

            <p className="section-intro-text">Stay updated with global trends that affect your investments. Be part of conversations that matter.</p>
            <div className="row mt-4 article-content-row">
                {articleList.map((article, index) => (
                    <div key={article?.id || `article-${index}`} className="col-lg-12 d-flex mb-4">
                        <div className="col-lg-8">
                            <div className="article-list">
                                <div className="article-item">
                                    <h3 className="article-title"><Link href={'/article/' + article?.slug} style={{ textDecoration: 'none', color: '#0C0C0C', cursor: 'pointer' }}>{article.title}</Link></h3>
                                    <p className="article-description">{convertIntoSubString(article.excerpt)}</p>
                                    <Link href={'/article/' + article?.slug} className="article-link">
                                        Read More
                                        <svg width="29" height="24" viewBox="0 0 29 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <g clipPath="url(#clip0_3491_1545)">
                                                <path d="M15.695 16.83L20.5 12L15.695 7.16998C15.6516 7.11299 15.5965 7.066 15.5333 7.03217C15.4702 6.99834 15.4005 6.97848 15.329 6.97392C15.2576 6.96935 15.1859 6.98021 15.119 7.00573C15.0521 7.03126 14.9914 7.07087 14.9411 7.12188C14.8909 7.17289 14.8521 7.23411 14.8275 7.30139C14.803 7.36867 14.7931 7.44045 14.7987 7.51186C14.8043 7.58327 14.8252 7.65265 14.8599 7.7153C14.8946 7.77795 14.9424 7.8324 15 7.87498L18.595 11.5L9.03 11.5C8.89739 11.5 8.77022 11.5527 8.67645 11.6464C8.58268 11.7402 8.53 11.8674 8.53 12C8.53 12.1326 8.58268 12.2598 8.67645 12.3535C8.77022 12.4473 8.89739 12.5 9.03 12.5L18.595 12.5L15 16.125C14.9065 16.2191 14.8543 16.3466 14.8547 16.4792C14.8552 16.6119 14.9083 16.739 15.0025 16.8325C15.0967 16.926 15.2241 16.9782 15.3568 16.9778C15.4894 16.9773 15.6165 16.9241 15.71 16.83H15.695Z" fill="#0C0C0C" />
                                            </g>
                                            <rect x="26" y="2.5" width="19" height="23" rx="9.5" transform="rotate(90 26 2.5)" stroke="#0C0C0C" />
                                            <defs>
                                                <clipPath id="clip0_3491_1545">
                                                    <rect x="25.5" y="3" width="18" height="22" rx="9" transform="rotate(90 25.5 3)" fill="white" />
                                                </clipPath>
                                            </defs>
                                        </svg>
                                    </Link>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-4">
                            <div className="article-images">
                                <div className="article-image-wrapper">
                                    <div className="article-image">
                                        {article?.image?.url ? (
                                            <Image src={article.image.url} alt={article.title || ""} width={400} height={250} />
                                        ) : (
                                            <div style={{ width: 400, height: 250, backgroundColor: '#f0f0f0' }} />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="text-center mt-5">
                <Link href={'/learn-investing'} className="btn btn-primary" style={{ textDecoration: 'none', cursor: 'pointer' }}>View More Posts</Link>
            </div>
        </>
    );
};

export default LearningCenterClient;
